<template>
  <div class="w-full rounded-md bg-white p-6 shadow-md">
    <div class="w-full space-y-4">
      <h2 v-if="comprobante" class="mb-2 text-center text-xl font-semibold text-gray-700">
        Comprobante nro. {{ comprobante.numero_comprobante }}
      </h2>
      <transition
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300"
        enter-class="opacity-0"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <Cargando v-if="isLoading" key="1" />
        <Empty key="2" v-else-if="errorCarga" :msg="errorCarga" />
        <Empty key="3" v-else-if="!comprobante" />
        <div v-else key="4">
          <div v-if="muestroSelectorComprobante" class="flex flex-wrap gap-x-6 gap-y-2 py-2">
            <div class="flex items-center space-x-2">
              <input
                v-model="tipoComprobante"
                value="ventanilla"
                type="radio"
                name="tipoComprobante"
                id="radio-ventanilla"
                class="h-4 w-4 rounded-full border-gray-400 text-colescba-blue-600 transition duration-150 ease-in-out focus:ring-colescba-blue-500"
              />
              <label for="radio-ventanilla">Ver comprobante para pago por ventanilla</label>
            </div>
            <div class="flex items-center space-x-2">
              <input
                v-model="tipoComprobante"
                value="otros"
                type="radio"
                name="tipoComprobante"
                id="radio-otros"
                class="h-4 w-4 rounded-full border-gray-400 text-colescba-blue-600 transition duration-150 ease-in-out focus:ring-colescba-blue-500"
              />
              <label for="radio-otros">Ver comprobante para otros medios de pago</label>
            </div>
          </div>
          <div class="w-full overflow-x-auto rounded-sm bg-zinc-500 p-2 shadow-lg">
            <div
              ref="printContent"
              class="mx-auto rounded-sm border border-gray-700 border-opacity-70 shadow-[0_0_10px] shadow-gray-800"
              :class="comprobanteWidth"
            >
              <component
                :is="componenteComprobante"
                :comprobante="comprobante"
                :deuda="deuda"
                :mediosPago="mediosPago"
                :tipoComprobante="tipoComprobante"
              />
            </div>
          </div>
        </div>
      </transition>
      <transition
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300"
        enter-class="opacity-0"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <div v-if="!isLoading" class="flex justify-center">
          <button
            class="mx-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm transition duration-150 ease-in-out hover:bg-cool-gray-200 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 active:bg-cool-gray-200"
            @click="volver"
          >
            Volver
          </button>
          <button
            @click="imprimir"
            type="button"
            v-if="!errorCarga && comprobante"
            class="inline-flex items-center justify-center space-x-2 rounded-md border border-blue-brand-700 bg-blue-brand-500 px-3 py-2 text-sm font-medium text-blue-brand-100 transition duration-150 ease-in-out hover:bg-blue-brand-700 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 active:bg-blue-brand-700"
          >
            <SolidPrinterIcon class="h-6 w-6 text-blue-brand-100" />
            <span class="text-center text-sm font-bold">Imprimir</span>
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { useService } from '@colescba/app-vue'
import { useSession } from '@colescba/ui'

import Cargando from '@/components/Core/Cargando'
import Empty from '@/components/Core/Empty'

// Para cambiar la orientacion de la pagina dependiendo el comprobante a imprimir
// see: https://stackoverflow.com/questions/11160260/can-javascript-change-the-value-of-page-css
var cssPagedMedia = (function () {
  var style = document.createElement('style')
  document.head.appendChild(style)
  return function (rule) {
    style.innerHTML = rule
  }
})()

cssPagedMedia.size = function (size) {
  cssPagedMedia('@page {size: ' + size + '}')
}

const configComprobantes = {
  TASASRC: {
    width: 'w-[21cm]',
    pageSize: 'A4',
  },
  CTER: {
    width: 'w-[29.7cm]',
    pageSize: 'A4 landscape',
  },
  ITA: {
    width: 'w-[29.7cm]',
    pageSize: 'A4 landscape',
  },
  DPPJ: {
    width: 'w-[21cm]',
    pageSize: 'A4',
  },
  HJ: {
    width: 'w-[21cm]',
    pageSize: 'A4',
  },
  CTANOT: {
    width: 'w-[21cm]',
    pageSize: 'A4',
  },
}

export default {
  name: 'VerComprobante',

  components: { Cargando, Empty },

  mixins: [useService('portalPagos'), useSession()],

  props: {
    nroComprobante: { type: [String, Number], required: true },
  },

  data() {
    return {
      comprobante: null,
      deuda: null,
      isLoading: true,
      mediosPago: [],
      tipoComprobante: null,
      errorCarga: null,
    }
  },

  async mounted() {
    try {
      this.comprobante = await this.portalPagos.getComprobante(
        this.user.cuitCuil.numero,
        this.nroComprobante
      )
    } catch (e) {
      this.comprobante = null
      const status = e?.response?.status || e?.status
      const body = e?.response?.data || e?.data || {}
      const msg =
        body.errors && body.errors.length ? body.errors[0] : body.message || 'Error al cargar el comprobante'
      this.errorCarga =
        status === 404
          ? 'No es posible realizar el pago. Este comprobante está asociado a otro usuario.'
          : msg
      this.isLoading = false
      return
    }
    let response = await this.portalPagos.getDeudas(
      this.user.cuitCuil.numero,
      `filter_name:FN_DeudaxComprobante,nro_comprobante:${this.comprobante.numero_comprobante.trim()}`,
      'id:desc'
    )
    this.deuda = response?.records?.[0] || null

    if (!this.deuda || !this.deuda.tipo || !this.deuda.tipo.categoria) {
      this.comprobante = null
      this.isLoading = false
      return
    }

    cssPagedMedia.size(this.pageSize)
    this.mediosPago = await this.portalPagos.getMediosPago(
      this.user.cuitCuil.numero,
      this.deuda.tipo.id.trim()
    )
    this.tipoComprobante = this.tienePagoPorVentanilla ? 'ventanilla' : 'otros'
    this.isLoading = false
  },

  computed: {
    categoriaId() {
      return this.deuda?.tipo?.categoria?.id?.trim?.() || null
    },
    configActual() {
      return this.categoriaId && configComprobantes[this.categoriaId]
        ? configComprobantes[this.categoriaId]
        : { width: 'w-[21cm]', pageSize: 'A4' }
    },
    componenteComprobante() {
      switch (this.categoriaId) {
        case 'TASASRC':
          return () => import('@/components/Comprobante/Comprobante75X')
        case 'CTER':
          return () => import('@/components/Comprobante/Comprobante185')
        case 'ITA':
          return () => import('@/components/Comprobante/Comprobante181')
        case 'DPPJ':
          return () => import('@/components/Comprobante/Comprobante17X')
        case 'HJ':
          return () => import('@/components/Comprobante/Comprobante160')
        case 'CTANOT':
          return () => import('@/components/Comprobante/Comprobante180')
        default:
          return null
      }
    },
    comprobanteWidth() {
      return this.configActual.width
    },
    pageSize() {
      return this.configActual.pageSize
    },
    tienePagoPorVentanilla() {
      return this.deuda.publicaciones.some(p => p.medio_pago.id.trim() == 'COMPRO')
    },
    tieneOtrosMedios() {
      return this.deuda.publicaciones.some(p => p.medio_pago.id.trim() != 'COMPRO')
    },
    muestroSelectorComprobante() {
      return (
        ['TASASRC'].includes(this.categoriaId) &&
        this.tienePagoPorVentanilla &&
        this.tieneOtrosMedios
      )
    },
  },

  methods: {
    volver() {
      if (this.errorCarga) {
        this.$router.push({ name: 'comprobantes' })
      } else {
        this.$router.go(-1)
      }
    },
    imprimir() {
      let printArea = document.getElementById('print-area')
      let printContent = this.$refs.printContent.innerHTML
      printArea.innerHTML = printContent
      window.print()
    },
  },
}
</script>
<style>
:root {
  --page-size: A4;
}

@media print {
  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
